import {
    BackLink,
    ButtonSecondary,
    Colors,
    Icon,
    ModalDialog,
    Spinner,
    Text,
} from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import { AssignedEmployeesList } from './AssignedEmployeesList';

import type { Competency } from '@/types/competency/Competency';

import { useGetCompetencyImpact } from '@/api/competency-services/competency';
import { useGetCompetencyProfiles } from '@/api/competency-services/competency-profile';
import { useGetIndividualEmployees } from '@/api/competency-services/employee-competency';
import {
    useGetProfileAssignments,
    useGetProfileAssignmentStatistics,
} from '@/api/competency-services/profile-assignment';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getTextByLanguage } from '@/utils/localization';

// A row describing a profile that contains the competency, with its competency and
// employee counts resolved client-side from the profiles + statistics read models.
type ProfileRow = {
    id: string;
    name: string;
    competencyCount: number;
    employeeCount: number;
};

// The in-modal employee drill-in target: a profile (with its name) or the
// individually-added employees section. `null` shows the profile list.
type DrillTarget = { kind: 'profile'; id: string; name: string } | { kind: 'individual' };

type AssignedProfilesModalProps = {
    /** The page mounts this dialog only while a competency is being inspected, so it opens by default. */
    isOpen?: boolean;
    competency: Competency;
    onClose: () => void;
};

// Fetches a profile's assignee ids, then renders the shared employee list.
const ProfileEmployees = ({ profileId }: { profileId: string }) => {
    const { data: assignments } = useGetProfileAssignments(profileId);
    const employeeIds = useMemo(
        () => (assignments ?? []).map((assignment) => assignment.employeeId),
        [assignments],
    );

    return (
        <AssignedEmployeesList
            employeeIds={employeeIds}
            cacheIdentifier={`profile-${profileId}`}
        />
    );
};

// Fetches the individually-added employee ids, then renders the shared employee list.
const IndividualEmployees = ({ competencyId }: { competencyId: string }) => {
    const { data: employeeIds } = useGetIndividualEmployees(competencyId);

    return (
        <AssignedEmployeesList
            employeeIds={employeeIds ?? []}
            cacheIdentifier={`individual-${competencyId}`}
        />
    );
};

export const AssignedProfilesModal = ({
    isOpen = true,
    competency,
    onClose,
}: AssignedProfilesModalProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    const [drillTarget, setDrillTarget] = useState<DrillTarget | null>(null);

    const competencyName = getTextByLanguage(competency.name, language);

    // Profiles that contain the competency (from the impact read model).
    const {
        data: impact,
        isLoading: isImpactLoading,
        isError: isImpactError,
        refetch: refetchImpact,
    } = useGetCompetencyImpact(competency.id);
    // The full profiles read model, joined client-side to read each profile's competency count.
    const { data: allProfiles } = useGetCompetencyProfiles();
    // Per-profile assigned-employee counts for the whole company (one fetch).
    const { data: statistics } = useGetProfileAssignmentStatistics();
    // Individually-added employee ids — same source as the drill-in list so they never disagree.
    const { data: individualEmployeeIds } = useGetIndividualEmployees(competency.id);

    const individualCount = individualEmployeeIds?.length ?? 0;

    const profileRows = useMemo<ProfileRow[]>(() => {
        const profilesById = new Map((allProfiles ?? []).map((profile) => [profile.id, profile]));
        const countByProfileId = new Map(
            (statistics ?? []).map((entry) => [entry.profileId, entry.count]),
        );

        return (impact?.profilesAffected ?? [])
            .map((affected) => {
                const profile = profilesById.get(affected.id);

                return {
                    id: affected.id,
                    // Prefer the live profiles read model name; fall back to the impact name.
                    name: profile
                        ? getTextByLanguage(profile.name, language)
                        : getTextByLanguage(affected.name, language),
                    competencyCount: profile?.items.length ?? 0,
                    employeeCount: countByProfileId.get(affected.id) ?? 0,
                };
            })
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    }, [impact?.profilesAffected, allProfiles, statistics, language]);

    const drillHeading =
        drillTarget?.kind === 'profile'
            ? drillTarget.name
            : t('competencies.assignedProfilesModal.individualSectionTitle');

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={onClose}
            closeAriaLabel={t('common.close')}
            title={t('competencies.assignedProfilesModal.title')}
            subtitle={t('competencies.assignedProfilesModal.subtitle', { name: competencyName })}
            size={'md'}
            backdropBlur
            dataTestId={'competency-assigned-profiles-modal'}
        >
            {drillTarget ? (
                // ---- Employee drill-in view (replaces the profile list in place). ----
                <div className={'flex flex-col gap-4'}>
                    <BackLink
                        label={t('competencies.assignedProfilesModal.back')}
                        onClick={() => setDrillTarget(null)}
                        dataTestId={'assigned-profiles-back'}
                    />

                    <p className={'text-navy text-base font-semibold'}>{drillHeading}</p>

                    {drillTarget.kind === 'profile' ? (
                        <ProfileEmployees profileId={drillTarget.id} />
                    ) : (
                        <IndividualEmployees competencyId={competency.id} />
                    )}
                </div>
            ) : (
                // ---- Profile list view. ----
                <div className={'flex flex-col gap-3'}>
                    {/* Individually-added row (special, light grey). */}
                    <div
                        className={
                            'border-base-100 bg-base-25 mb-2 flex flex-col items-center justify-between gap-4 rounded-xl border px-3 py-3 sm:flex-row'
                        }
                    >
                        <div className={'flex min-w-0 items-center gap-1'}>
                            <span className={'flex h-8 w-8 shrink-0 items-center justify-center'}>
                                <Icon
                                    name={'users-user-profile-add-2'}
                                    colorClassName={'text-base-400'}
                                    ariaHidden
                                />
                            </span>

                            <Text
                                size={'small'}
                                color={Colors.NAVY}
                                className={'min-w-0 font-medium'}
                            >
                                {t('competencies.assignedProfilesModal.individualRow', {
                                    count: individualCount,
                                })}
                            </Text>
                        </div>

                        <ButtonSecondary
                            size={'sm'}
                            onClick={() => setDrillTarget({ kind: 'individual' })}
                            rightIcon={
                                <Icon
                                    name={'arrow-arrow-right-1'}
                                    size={'sm'}
                                    ariaHidden
                                />
                            }
                            dataTestId={'assigned-profiles-view-individual'}
                        >
                            {t('competencies.assignedProfilesModal.view')}
                        </ButtonSecondary>
                    </div>

                    {isImpactLoading ? (
                        <div className={'flex items-center justify-center py-12'}>
                            <Spinner />
                        </div>
                    ) : isImpactError ? (
                        <div
                            className={
                                'flex flex-col items-center justify-center gap-4 py-10 text-center'
                            }
                        >
                            <p className={'text-error text-sm'}>
                                {t('competencies.profiles.loadError')}
                            </p>

                            <ButtonSecondary
                                size={'sm'}
                                onClick={() => void refetchImpact()}
                                dataTestId={'assigned-profiles-retry'}
                            >
                                {t('competencies.retry')}
                            </ButtonSecondary>
                        </div>
                    ) : profileRows.length === 0 ? (
                        <p className={'text-base-500 py-10 text-center text-base'}>
                            {t('competencies.assignedProfilesModal.profilesEmpty')}
                        </p>
                    ) : (
                        <ul className={'flex flex-col gap-3'}>
                            {profileRows.map((profile) => (
                                <li
                                    key={profile.id}
                                    className={
                                        'border-base-100 flex flex-col items-center justify-between gap-4 rounded-xl border bg-white px-3 py-2 sm:flex-row sm:px-4'
                                    }
                                    data-testid={`assigned-profiles-profile-${profile.id}`}
                                >
                                    <div className={'min-w-0'}>
                                        <p className={'text-md text-navy truncate font-semibold'}>
                                            {profile.name}
                                        </p>

                                        <Text
                                            size={'small'}
                                            color={Colors.BASE_500}
                                            className={'mt-0.5'}
                                            truncate
                                        >
                                            {t('competencies.assignedProfilesModal.profileMeta', {
                                                competencies: t(
                                                    'competencies.assignedProfilesModal.profileCompetencyCount',
                                                    { count: profile.competencyCount },
                                                ),
                                                employees: t(
                                                    'competencies.assignedProfilesModal.profileEmployeeCount',
                                                    { count: profile.employeeCount },
                                                ),
                                            })}
                                        </Text>
                                    </div>

                                    <ButtonSecondary
                                        size={'sm'}
                                        fitContent
                                        onClick={() =>
                                            setDrillTarget({
                                                kind: 'profile',
                                                id: profile.id,
                                                name: profile.name,
                                            })
                                        }
                                        leftIcon={
                                            <Icon
                                                name={'users-profile-2user'}
                                                size={'sm'}
                                                ariaHidden
                                            />
                                        }
                                        rightIcon={
                                            <Icon
                                                name={'arrow-arrow-right-1'}
                                                size={'sm'}
                                                ariaHidden
                                            />
                                        }
                                        dataTestId={`assigned-profiles-view-profile-${profile.id}`}
                                    >
                                        {t('competencies.assignedProfilesModal.viewEmployees')}
                                    </ButtonSecondary>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </ModalDialog>
    );
};
